import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import {
  Network,
  Layers,
  Workflow,
  Braces,
  GitMerge,
  ShieldCheck,
  ChevronDown,
  type LucideIcon,
} from "lucide-react";
import { TextReveal } from "./animations";

type Layer = {
  n: string;
  label: string;
  Icon: LucideIcon;
  principle: string;
  detail: string;
  stack: string;
  context: string;
  outcome: string;
};

const LAYERS: Layer[] = [
  {
    n: "01",
    label: "Retrieval",
    Icon: Network,
    principle: "Reason from the organization's own evidence.",
    detail:
      "Start with ontology, then retrieval. The system answers from internal records, brand libraries, product knowledge and decision history, not the open web's average.",
    stack: "Ontology · embeddings · vector retrieval · graph traversal",
    context: "Knowledge bases, brand libraries, product archives and research repositories",
    outcome: "Internal knowledge becomes searchable, reusable and defensible across teams",
  },
  {
    n: "02",
    label: "Fine-tuning",
    Icon: Layers,
    principle: "Teach the model the house language.",
    detail:
      "Fine-tuned models and adapters trained on an organization's visual, tonal and structural standards, so generation starts closer to the brand instead of generic output.",
    stack: "LoRA training · ControlNet · conditioning stacks · seed control",
    context: "Visual generation systems and brand-aware creative pipelines",
    outcome: "Generated assets follow brand grammar with less manual correction",
  },
  {
    n: "03",
    label: "Agents",
    Icon: Workflow,
    principle: "Orchestrate outcomes, not prompts.",
    detail:
      "Multi-step agents with explicit state, fallbacks and audit trails, built to own repeatable work without disappearing into a black box.",
    stack: "Tool calling · state machines · fallbacks · audit trails",
    context: "Workflow automation across creative, product and operating teams",
    outcome: "Repeated work moves from manual handoff to governed execution",
  },
  {
    n: "04",
    label: "Schemas",
    Icon: Braces,
    principle: "Generate output the system can use.",
    detail:
      "Schema-first prompting with typed inputs, constrained generation and validated returns, so AI output can move directly into products, workflows and publishing systems.",
    stack: "Structured output · JSON schema · validators · eval fixtures",
    context: "Content pipelines, asset generation and API integrations",
    outcome: "Production volume increases without losing editorial or product standards",
  },
  {
    n: "05",
    label: "Decisions",
    Icon: GitMerge,
    principle: "Design the surface where judgment happens.",
    detail:
      "Reasoning made legible: tradeoffs exposed, evidence traceable and decisions reversible when the model, data or assumption turns out to be wrong.",
    stack: "Reasoning graphs · provenance trails · reversible state",
    context: "Decision support systems, strategy tooling and leadership interfaces",
    outcome: "Decision history becomes navigable as a system, not buried in a changelog",
  },
  {
    n: "06",
    label: "Evaluation",
    Icon: ShieldCheck,
    principle: "Gate quality before it reaches the user.",
    detail:
      "Model selection, eval sets, regression checks and release gates built around real tasks, not synthetic demos.",
    stack: "Model selection · eval sets · regression checks · release gates",
    context: "Quality assurance for shipped AI products and production workflows",
    outcome: "AI output holds up at enterprise standard in production",
  },
];

const easeStandard = [0.25, 0.4, 0.25, 1] as const;

const AITransformation = () => {
  const [expanded, setExpanded] = useState(false);
  const [active, setActive] = useState(0);
  const layer = LAYERS[active];
  const ActiveIcon = layer.Icon;

  return (
    <section
      id="ai-transformation"
      aria-labelledby="ai-transformation-heading"
      className="border-b border-border"
    >
      {/* Section header */}
      <div className="px-6 md:px-12 py-8 md:py-10 flex flex-col md:flex-row md:items-center justify-between gap-6 md:gap-12 border-b border-border">
        <TextReveal delay={0.2}>
          <h2
            id="ai-transformation-heading"
            className="font-display text-[clamp(1.75rem,3.6vw,3rem)] leading-[1] uppercase tracking-tight text-foreground md:max-w-[14ch]"
          >
            AI Systems, Applied
          </h2>
        </TextReveal>

        <div className="flex flex-col md:flex-row md:items-center gap-5 md:gap-10 md:flex-1 md:justify-end">
          <motion.p
            className="text-sm md:text-[0.95rem] text-muted-foreground max-w-sm leading-relaxed"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            Six practical layers for turning AI from isolated experiments into reliable product and creative infrastructure.
          </motion.p>

          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            aria-expanded={expanded}
            aria-controls="ai-framework-layers"
            className="group inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-accent/80 hover:text-accent transition-colors shrink-0 self-start md:self-auto"
          >
            <span className="border-b border-dashed border-accent/40 group-hover:border-accent pb-0.5">
              {expanded ? "Close framework" : "Explore framework"}
            </span>
            <motion.span
              animate={{ rotate: expanded ? 180 : 0 }}
              transition={{ duration: 0.3, ease: easeStandard }}
              className="inline-flex"
            >
              <ChevronDown className="w-3.5 h-3.5" strokeWidth={1.75} />
            </motion.span>
          </button>
        </div>
      </div>


      {/* Interactive layered model */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            id="ai-framework-layers"
            key="ai-framework-layers"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: easeStandard }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-1 md:grid-cols-[minmax(260px,340px)_1fr]">
              {/* Layer index */}
              <ul
                role="tablist"
                aria-label="AI-native operating layers"
                className="border-b md:border-b-0 md:border-r border-border"
              >
                {LAYERS.map((l, i) => {
                  const isActive = i === active;
                  const Icon = l.Icon;
                  return (
                    <li key={l.n}>
                      <button
                        role="tab"
                        aria-selected={isActive}
                        onMouseEnter={() => setActive(i)}
                        onFocus={() => setActive(i)}
                        onClick={() => setActive(i)}
                        className={[
                          "group w-full text-left px-6 md:px-8 py-5 flex items-center gap-4 border-b border-border last:border-b-0 transition-colors duration-300",
                          isActive
                            ? "bg-foreground/[0.03]"
                            : "hover:bg-foreground/[0.02]",
                        ].join(" ")}
                      >
                        <span
                          className={[
                            "font-mono text-[10px] tracking-[0.2em] transition-colors duration-300 w-6",
                            isActive ? "text-foreground" : "text-muted-foreground/60",
                          ].join(" ")}
                        >
                          {l.n}
                        </span>
                        <Icon
                          strokeWidth={1}
                          className={[
                            "h-4 w-4 transition-colors duration-300",
                            isActive ? "text-foreground" : "text-muted-foreground/60",
                          ].join(" ")}
                        />
                        <span
                          className={[
                            "font-display text-base uppercase tracking-tight transition-colors duration-300 flex-1",
                            isActive ? "text-foreground" : "text-muted-foreground",
                          ].join(" ")}
                        >
                          {l.label}
                        </span>
                        <motion.span
                          aria-hidden
                          className="block h-px bg-foreground"
                          initial={false}
                          animate={{ width: isActive ? 28 : 0 }}
                          transition={{ duration: 0.4, ease: easeStandard }}
                        />
                      </button>
                    </li>
                  );
                })}
              </ul>

              {/* Detail panel */}
              <div className="relative px-6 md:px-12 py-10 md:py-14 min-h-[420px] flex flex-col justify-between">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={layer.n}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.4, ease: easeStandard }}
                    className="flex flex-col gap-8"
                  >
                    <div className="flex items-center gap-4">
                      <span className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground/60">
                        Layer {layer.n} / 06
                      </span>
                      <span className="h-px flex-1 bg-border" />
                      <span className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground/60 uppercase">
                        {layer.label}
                      </span>
                    </div>

                    <div className="flex items-start gap-6">
                      <div className="shrink-0 h-14 w-14 border border-border rounded-sm flex items-center justify-center">
                        <ActiveIcon
                          strokeWidth={1}
                          className="h-6 w-6 text-foreground"
                        />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-display text-[clamp(1.75rem,3vw,2.5rem)] uppercase tracking-tight leading-[1] text-foreground">
                          {layer.principle}
                        </h3>
                        <p className="mt-4 text-sm md:text-base text-muted-foreground leading-relaxed max-w-xl">
                          {layer.detail}
                        </p>
                      </div>
                    </div>

                    {/* Metadata strip */}
                    <div className="grid grid-cols-1 md:grid-cols-3 border-t border-border pt-6 gap-6 md:gap-0">
                      <div className="md:pr-6 md:border-r border-border">
                        <div className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground/60 mb-2">
                          Stack
                        </div>
                        <div className="font-mono text-[11px] text-foreground/80 leading-relaxed">
                          {layer.stack}
                        </div>
                      </div>
                      <div className="md:px-6 md:border-r border-border">
                        <div className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground/60 mb-2">
                          Typical use
                        </div>
                        <div className="text-sm text-foreground leading-snug">
                          {layer.context}
                        </div>
                      </div>
                      <div className="md:pl-6">
                        <div className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground/60 mb-2">
                          Outcome
                        </div>
                        <div className="text-sm text-muted-foreground leading-snug">
                          {layer.outcome}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default AITransformation;
